import mixpanel from 'mixpanel-browser'
import * as Sentry from '@sentry/react'

const MIXPANEL_TOKEN = import.meta.env.VITE_MIXPANEL_TOKEN
const MIXPANEL_ENABLED = import.meta.env.VITE_MIXPANEL_ENABLED === 'true'
const MIXPANEL_DEBUG = import.meta.env.VITE_MIXPANEL_DEBUG === 'true'

let isInitialized = false

/** Opt-in console output for checking events locally (VITE_MIXPANEL_DEBUG). */
function debugLog(...args: unknown[]): void {
  if (!MIXPANEL_DEBUG) return
  // eslint-disable-next-line no-console -- deliberate opt-in debug output, enabled only by VITE_MIXPANEL_DEBUG
  console.log(...args)
}

export function initializeMixpanel(): void {
  if (isInitialized) return

  if (!MIXPANEL_TOKEN) {
    if (MIXPANEL_DEBUG) {
      console.warn('[Mixpanel] Missing VITE_MIXPANEL_TOKEN - tracking disabled')
    }
    isInitialized = true
    return
  }

  if (!MIXPANEL_ENABLED) {
    debugLog('[Mixpanel] Tracking disabled (VITE_MIXPANEL_ENABLED is not true)')
    debugLog('[Mixpanel] Events will be logged to console only')
    isInitialized = true
    return
  }

  mixpanel.init(MIXPANEL_TOKEN, {
    debug: MIXPANEL_DEBUG,
    track_pageview: false,
    persistence: 'localStorage',
    api_host: 'https://api-eu.mixpanel.com',
  })

  isInitialized = true
  debugLog('[Mixpanel] Initialized')
}

/** Low-level Mixpanel track. Domain tracking belongs in `lib/track`. */
export function trackMixpanel(eventName: string, props: Record<string, unknown> = {}): void {
  debugLog('[Mixpanel Event]', eventName, props)

  if (!MIXPANEL_TOKEN || !MIXPANEL_ENABLED || !isInitialized) return

  try {
    mixpanel.track(eventName, props)
  } catch (error) {
    Sentry.captureException(error, {
      tags: { mixpanel_event: eventName },
    })
  }
}

/** Identify the current user (by hashed id) for cross-session funnel tracking. */
export function identifyMixpanelUser(id: string): void {
  if (!id || !MIXPANEL_TOKEN || !MIXPANEL_ENABLED || !isInitialized) return
  try {
    mixpanel.identify(id)
  } catch (error) {
    Sentry.captureException(error, {
      tags: { mixpanel_function: 'identify' },
    })
  }
}
